/**
 * How far past its start the playhead must be before the picture counts as
 * showing: a little over half a frame at 30 fps, so the FIRST frame after
 * a start never qualifies but the second one does.
 */
const MIN_ADVANCE_S = 0.02;

/**
 * Reports whether `video` is actually showing moving picture, and reports it
 * again the moment it loses its picture.
 *
 * This decides when a poster cover may lift, and none of the obvious signals
 * are it:
 * - `canplay` / `loadeddata` say a frame is decoded, not shown. iOS fires
 *   them while the element still paints black.
 * - The `poster` attribute is no cover at all on iOS: WebKit drops it the
 *   instant `play()` is called and paints black until the first frame lands,
 *   which on a cold HLS start is ~0.8 s — the black card on the class rail.
 * - A one-way "ready" flag goes stale: the proximity scheduler releases a
 *   player that scrolls out of its budget (`removeAttribute("src")` +
 *   `load()`) and re-attaches it on the way back, and a flag set during the
 *   first visit showed black for the whole second cold start.
 * - Even `requestVideoFrameCallback` alone is too early on iOS. On the
 *   native (AVFoundation) path it fires at `loadeddata` with the clock still
 *   at 0.00, 100–250 ms before the layer draws anything; lifting the cover
 *   there was the short black flash left over when swiping fast.
 *
 * So the rule is: a frame presented WHILE PLAYING, from a playhead that has
 * moved past where this start began. A player that is merely warmed up
 * (paused on its first frame) keeps its poster — the poster is frame 0, so
 * that costs nothing. `emptied` / `loadstart` mean the picture is gone
 * (release, re-attach, hls.js attaching MSE, a watchdog recovery), so the
 * cover comes back.
 */
export function watchVideoFrame(
  video: HTMLVideoElement,
  onChange: (hasFrame: boolean) => void,
) {
  // Typed as always present, but missing before Safari 15.4 / Firefox 132.
  const supported = typeof video.requestVideoFrameCallback === "function";
  let hasFrame = false;
  let handle: number | undefined;
  let disposed = false;
  /** Where the current start began; the picture counts once it moves past. */
  let from = 0;

  const set = (next: boolean) => {
    if (disposed || hasFrame === next) return;
    hasFrame = next;
    onChange(next);
  };
  const cancel = () => {
    if (handle !== undefined) video.cancelVideoFrameCallback(handle);
    handle = undefined;
  };
  const moved = (time: number) => !video.paused && time > from + MIN_ADVANCE_S;
  /** Re-requests on every signal: a request made before a source swap may never fire. */
  const arm = () => {
    if (hasFrame || disposed || !supported) return;
    cancel();
    handle = video.requestVideoFrameCallback((_now, frame) => {
      handle = undefined;
      if (moved(frame.mediaTime)) set(true);
      // Still the first frame of a start, or a frame shown while paused:
      // keep watching for as long as it plays.
      else if (!video.paused) arm();
    });
  };
  /** A new start: count movement from here, not from 0:00. */
  const restart = () => {
    from = video.currentTime;
    arm();
  };
  const lose = () => {
    from = 0;
    set(false);
    arm();
  };
  /** Without the API, a clock that has moved while playing is the next best proof. */
  const onTimeUpdate = () => {
    if (!supported && moved(video.currentTime)) set(true);
  };

  const RESTART_EVENTS = ["play", "seeked"] as const;
  const LOSE_EVENTS = ["emptied", "loadstart"] as const;
  for (const event of RESTART_EVENTS) video.addEventListener(event, restart);
  for (const event of LOSE_EVENTS) video.addEventListener(event, lose);
  video.addEventListener("playing", arm);
  video.addEventListener("timeupdate", onTimeUpdate);
  arm();

  return () => {
    disposed = true;
    cancel();
    for (const event of RESTART_EVENTS)
      video.removeEventListener(event, restart);
    for (const event of LOSE_EVENTS) video.removeEventListener(event, lose);
    video.removeEventListener("playing", arm);
    video.removeEventListener("timeupdate", onTimeUpdate);
  };
}
